/**
 * Dismissal of the static boot splash declared in `index.html`.
 *
 * The splash (`#boot-splash`) is painted before any JS loads and lives
 * outside `#root`, so React's `createRoot` never wipes it. It stays on top
 * of the app — hiding every intermediate `<BootSplash>` flash while auth
 * resolves and route chunks lazy-load — until the first real screen mounts
 * (`SignInPage` for the unauthenticated path, `AuthenticatedLayout` for the
 * authenticated one), which calls `dismissBootSplash()`.
 *
 * To keep the loading indicator a deliberate beat rather than a flash on warm
 * boots, dismissal enforces a minimum on-screen time
 * ({@link MIN_BOOT_SPLASH_VISIBLE_MS}); boots slower than that floor dismiss
 * with no added delay.
 *
 * @agentic connections
 */

/** ID of the static splash element in `index.html`. @agentic connections */
const BOOT_SPLASH_ID = "boot-splash";

/** Class that fades the splash out before removal. @agentic connections */
const HIDDEN_CLASS = "boot-splash--hidden";

/** Fallback removal delay (ms) if `transitionend` never fires (e.g. reduced
 *  motion, or the element is not laid out). Slightly above the 250ms CSS
 *  fade. @agentic connections */
const REMOVE_FALLBACK_MS = 400;

/**
 * Minimum time (ms) the splash stays on screen before it is allowed to fade.
 * On a warm boot the first real screen can mount in a few hundred ms, which
 * reduces the splash to a jarring flash. Holding it for this floor makes the
 * loading indicator a deliberate, perceivable beat. Boots slower than this
 * floor incur no added delay — the splash dismisses as soon as it can.
 * @agentic connections */
export const MIN_BOOT_SPLASH_VISIBLE_MS = 900;

/** Dataset flag marking a splash whose dismissal has already begun, so repeat
 *  calls during the minimum-visible hold don't schedule duplicate fades.
 *  Lives on the element (not module state) so a fresh splash resets it.
 *  @agentic connections */
const DISMISSING_ATTR = "dismissing";

/**
 * Elapsed time (ms) since navigation start, which is when the static splash
 * (inlined in `index.html`) first paints. Used to gauge how long the splash
 * has been visible. Falls back to {@link MIN_BOOT_SPLASH_VISIBLE_MS} when no
 * high-resolution clock is available so no artificial delay is introduced.
 * @agentic connections */
const splashVisibleForMs = (): number =>
  typeof performance !== "undefined" && typeof performance.now === "function"
    ? performance.now()
    : MIN_BOOT_SPLASH_VISIBLE_MS;

/** Fade out and remove the splash element. @agentic connections */
const fadeOutAndRemove = (splash: HTMLElement): void => {
  splash.classList.add(HIDDEN_CLASS);

  let removed = false;
  const remove = (): void => {
    if (removed) return;
    removed = true;
    splash.remove();
  };

  splash.addEventListener("transitionend", remove, { once: true });
  // Guard against `transitionend` never firing (no transition under reduced
  // motion / display) so the overlay can't get stuck capturing the screen.
  if (typeof window !== "undefined") {
    window.setTimeout(remove, REMOVE_FALLBACK_MS);
  } else {
    remove();
  }
};

/**
 * Fade out and remove the static boot splash, enforcing a minimum on-screen
 * time ({@link MIN_BOOT_SPLASH_VISIBLE_MS}) so a fast boot doesn't flash the
 * loading indicator. If the splash hasn't been visible long enough, the fade
 * is deferred for the remaining time; otherwise it starts immediately.
 *
 * Idempotent and safe to call when the splash is absent (tests, repeat
 * invocations, or after it has already been removed).
 * @agentic connections
 */
export const dismissBootSplash = (): void => {
  if (typeof document === "undefined") return;
  const splash = document.getElementById(BOOT_SPLASH_ID);
  if (!splash) return;
  if (splash.dataset[DISMISSING_ATTR] === "true") return;
  splash.dataset[DISMISSING_ATTR] = "true";

  const remaining = MIN_BOOT_SPLASH_VISIBLE_MS - splashVisibleForMs();
  if (remaining > 0 && typeof window !== "undefined") {
    window.setTimeout(() => fadeOutAndRemove(splash), remaining);
    return;
  }

  fadeOutAndRemove(splash);
};
